import React, { ReactNode, useMemo, useRef, useState } from 'react';
import {
  AddOperandButton,
  Filter,
  FilterStyledPopper,
  getOperatorColor,
  OpenSelect,
  RadioButton,
  Select,
  SelectTrigger,
  StringConditionType,
  TextInput,
  useClickOutside,
} from '@fabric-ui/components';
import { AsyncGetOptions, OptionQueryType } from '@fabric-ui/components/Table';
import { Delete } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { emptyArray, getIfValidStringifiedObject, noop, uniqBy } from '@fabric-ui/utils';
import { Box, Button, Divider, IconButton, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { add, addDays, addMonths, addWeeks, format, isEqual, subDays, subMonths, subWeeks } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../context/AvContextProvider';
import { useAsyncFilterOptions } from '../hooks/useAsyncFilterOptions';
import { useDebounceV1 } from '../hooks/useDebounce';
import { cleanEmptyFilters, filterToExpression, sortBySeverity } from '../utils/filterUtils';
import { generateOption, getEscapedValue, getNestedValue, isJSON, orderByKey, renameProp, setNestedValue } from '../utils/utils';
import { DwQueryRequest, SortDir } from '../views/CustomDashboards/types';
import { useMeasurementsList } from '../views/Reports/hooks';
import { SchemaTypes, typeLabelMap } from '../views/Sources/Mapping/types';
import { useBuildWhereClause } from '../views/Tickets/hooks';
import AvDateRangePicker from './DatePicker/AvDateRangePicker';
import { epochDateString, PeriodBehavior } from './DatePicker/AvDateRangePicker.constants';
import { isDateRangeEmpty, isDateRangeNotEmpty, isDateRangeValue } from './DatePicker/utils';
import {
  booleanOptions,
  ConditionType,
  defaultOperator,
  defaultValue,
  filterOperatorOptions,
  FilterOption,
  getFilterOptionsWithLegacy,
  getOperator,
  isNullOperator,
  operatorLabels,
  rawExpressionToFilter,
} from './filters/utils';
import { IconVariant, ItemWithLogo, SeverityItem } from './ItemWithLogo';

interface FieldFilterProps {
  activeProjName?: string;
  field: string;
  label?: ReactNode;
  filters?: Record<string, any>;
  isEnabled: boolean;
  onChange: (field, value) => void;
  onFilterChange?: (filter: Filter) => void;
  onOpen?: () => void;
  onClose?: () => void;
  setRef?: (ref: any) => void;
  getOptions?: (data: any, label: ReactNode, v) => any[];
  overrideExtra?: string[];
  size?: string;
  disabled?: boolean;
  trigger?: any;
  fieldType?: keyof typeof typeLabelMap;
  options?: any[];
  isMultipleCondition?: boolean;
  fieldMap?: Record<string, any>;
  queryKey?: string;
  getValue?: (v) => ReactNode;
  isFilterExpression?: boolean;
  usage?: 'SPF' | undefined;
  isGlobalFilter?: boolean;
  selectIcon?: ReactNode;
  enableQueryObj?: boolean;
  onSearchChange?: (search: string) => void;
  asyncGetOptions?: AsyncGetOptions;
  extraOperands?: Filter[];
  isIngressField?: boolean;
  disablePortalInOperatorSelect?: boolean;
}

const getDisplayValue = value => (isJSON(value) ? (value.from || value.to ? `${value.from || ''}-${value.to || ''}` : '') : value);
const getField = (field: unknown): Array<unknown> | undefined => (field ? (Array.isArray(field) ? field : [field]) : undefined);

export const FieldFilter: React.FC<FieldFilterProps> = ({
  onChange,
  onFilterChange,
  field,
  filters = {},
  isEnabled,
  setRef = noop,
  getOptions = (data, label) =>
    data.map(({ value }) => (value === null || value?.length === 0 ? { title: `No ${label}`, value: null } : generateOption(value))),
  label = '',
  overrideExtra,
  size = 'small',
  disabled,
  trigger,
  fieldType,
  options,
  isMultipleCondition = true,
  onOpen = noop,
  onClose = noop,
  activeProjName,
  queryKey,
  getValue,
  isFilterExpression = false,
  usage,
  isGlobalFilter = false,
  selectIcon,
  enableQueryObj = false,
  asyncGetOptions,
  extraOperands,
  isIngressField,
  disablePortalInOperatorSelect = true,
}) => {
  const { palette } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { aggProjs, fieldMap } = useAvContext().accountEntities;
  const typeBase =
    fieldType || typeLabelMap[fieldMap[field]?.type as string | number] || (field.toLowerCase().endsWith('count') ? 'number' : 'string');
  const type = `${typeBase}${fieldMap[field]?.repeated ? '[]' : ''}`;
  const isSchemaField = !options;
  const isBooleanField = type === 'bool';
  const isDateField = ['date', SchemaTypes.DATETIME].includes(type);
  const supportedType = !['["MESSAGE"]', 'MESSAGE'].includes(fieldMap?.[field]?.type?.toString());
  const isSeverity = field.split('.')[1] === 'severity';
  const isNumberField = type === 'number';
  const filterObject = getIfValidStringifiedObject(filters[field]);
  const shouldShowMinutesAndHours = isDateField && fieldsToIncludeMinutesAndHours.has(field);
  // TODO: Remove when AVA-12635 is implemented.
  // When null isn't first in the array it is parsed incorrectly
  const value = filterObject || getField(filters[field])?.map(val => (val === 'null' ? null : val)) || [];

  const [mode, setMode] = useState(filterObject ? 'condition' : 'list');
  const initialFilter = emptyOperand[type];
  const [conditionValue, setConditionValue] = useState(filterObject ? value : { [isNumberField ? 'or' : 'and']: [initialFilter] });
  const operator = Object.keys(conditionValue)[0] as 'and' | 'or';
  const valueOperator = Object.keys(filterObject)[0];
  const buttonRef = useRef(null);
  const popperInnerRef = useRef(null);
  const [errors, setErrors] = useState<typeof conditionValue>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounceV1(search, 500);
  const escapedValue = getEscapedValue(debouncedValue);
  const searchCondition = debouncedValue ? `value LIKE '%${escapedValue}%'` : undefined;

  const handleSelectConditionChange = (value: unknown, index: number) => {
    setErrors([]);
    setConditionValue(prev => {
      const nestedPrev = getNestedValue(`${operator}.${index}`, prev);
      const newValue = isNullOperator(value) ? '' : nestedPrev.value;
      return setNestedValue(`${operator}.${index}`, prev, { operator: value, value: newValue });
    });
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  useClickOutside(popperInnerRef, handleClose);

  const whereClause =
    isSchemaField && activeProjName
      ? useBuildWhereClause({
          filters: isGlobalFilter ? { [field]: [] } : { ...filters, [field]: [] },
          // @ts-ignore
          extra: overrideExtra ? [...overrideExtra, searchCondition] : searchCondition,
          entityTypeId: (isGlobalFilter ? fieldMap[field] : aggProjs[activeProjName])?.entityTypeId?.name,
          fieldMap,
        })
      : '';

  const finalWhereClause = useMemo(
    () => whereClause,
    [isSchemaField, activeProjName, fieldMap, filters, field, searchCondition, overrideExtra, isGlobalFilter, aggProjs]
  );

  const getFieldQuery = field => {
    const fieldQuery = isGlobalFilter ? `${activeProjName}.${field.split('.').slice(1).join('.')}` : field;
    const explodeField = fieldMap?.[field]?.repeated ? `EXPLODE(${fieldQuery})` : fieldQuery;
    return isIngressField && activeProjName
      ? explodeField.replace(aggProjs[activeProjName].pathAlias, `${aggProjs[activeProjName]?.pathAlias}.sources`)
      : explodeField;
  };

  const fieldQuery = getFieldQuery(field);
  const sql = `SELECT distinct ${fieldQuery} as value
    FROM ${activeProjName}
    ${finalWhereClause} ORDER BY value ASC limit 800`;

  const { data: measurements = [] } = useMeasurementsList({ entityName: aggProjs[activeProjName ?? '']?.entityTypeId?.name });

  const typeMap = useMemo(
    () => measurements.reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), aggProjs[activeProjName ?? '']?.typeMap),
    [measurements, activeProjName]
  );

  const fieldQueryObj = useMemo(() => {
    const filtersObj = typeMap
      ? cleanEmptyFilters(filterToExpression(typeMap, isGlobalFilter ? { [field]: [] } : { ...filters, [field]: [] }))
      : undefined;
    return {
      select: { dims: [{ name: fieldQuery, alias: 'value' }], metrics: [] },
      filter:
        activeProjName && (escapedValue || filtersObj || extraOperands)
          ? {
              and: {
                operands: [
                  ...(escapedValue ? [{ expression: { fieldName: 'value', stringCondition: { contains: escapedValue } } }] : []),
                  ...(filtersObj ? [filtersObj] : []),
                  ...(extraOperands || []),
                ],
              },
            }
          : undefined,
      top: { size: 800, groupOthers: false },
      sorting: [{ name: 'value', dir: SortDir.ASC }],
      ...(activeProjName ? { projectionId: aggProjs[activeProjName]?.projId } : {}),
      distinct: true,
    };
  }, [fieldQuery, activeProjName, typeMap, isGlobalFilter, filters, escapedValue]);

  const queryObjectOptions = {
    options: {
      type: OptionQueryType.Object,
      queryObject: fieldQueryObj as DwQueryRequest,
      debounceQueries: false,
      onSuccess: data => sortBySeverity({ data, severityFieldName: fieldQuery, key: 'value' }),
      totalRowCount: true,
    },
    isEnabled:
      isEnabled &&
      mode === 'list' &&
      open &&
      !isDateField &&
      !isBooleanField &&
      supportedType &&
      !isNumberField &&
      isSchemaField &&
      enableQueryObj,
    placeholderData: keepPreviousData,
  };

  const querySqlOptions = {
    options: {
      type: OptionQueryType.SQL,
      key: queryKey ?? sql,
      sql,
      debounceQueries: false,
      placeholderData: keepPreviousData,
      onSuccess: data => sortBySeverity({ data, severityFieldName: fieldQuery, key: 'value' }),
      usage,
      showTotals: true,
    },
    isEnabled:
      isEnabled &&
      mode === 'list' &&
      open &&
      !isDateField &&
      !isBooleanField &&
      supportedType &&
      !isNumberField &&
      isSchemaField &&
      !enableQueryObj,
  };

  const asyncFilterOptions = useMemo(
    () =>
      asyncGetOptions
        ? {
            options: asyncGetOptions?.(escapedValue),
            isEnabled: open,
          }
        : undefined,
    [asyncGetOptions, escapedValue, open]
  );
  const {
    data: filterOptions = emptyArray,
    isLoading: isLoadingOptions,
    totalRowCount,
  } = useAsyncFilterOptions(asyncFilterOptions || (enableQueryObj ? queryObjectOptions : querySqlOptions));

  const memoOptions = useMemo(
    () =>
      options
        ? orderByKey(
            options.filter(o => (isBooleanField ? true : o.value?.toLowerCase?.().includes(search.toLowerCase()))),
            'title'
          )
        : uniqBy(
            getOptions([...(value.map?.(generateOption) || []), ...filterOptions], label, getValue).map(({ title, value }) => ({
              value,
              title: value === null ? 'Empty' : title,
            })),
            f => f.value
          ),
    [filterOptions, open, search, options]
  );
  const tooltipValue = useMemo(
    () =>
      filterObject ? (
        isDateField ? (
          <div>{formatDateValue({ ...filterObject })}</div>
        ) : (
          <div>{filterObject[valueOperator].map((operand, index, arr) => conditionTooltip(operand, index, arr, operator))}</div>
        )
      ) : (
        value?.join(', ')
      ),
    [value, conditionValue, filterObject, operator, getValue]
  );
  const calcOptions = field === 'finding.state' ? findingStateOptions : isBooleanField && isSchemaField ? booleanOptions : memoOptions;

  const getErrors = (val, operator) =>
    !operator ? ['Select Operator'] : filterOperatorOptions[type].find(({ value }) => value === operator)?.getErrors?.(val);

  const isSelectTrigger = !trigger || trigger.type.name === 'Select';
  const displayValue = useMemo(
    () =>
      filterObject
        ? isDateField
          ? formatDateValue({ ...filterObject })
          : `${operatorLabels[filterObject[valueOperator][0]?.operator]}  ${getDisplayValue(filterObject[valueOperator][0]?.value)}`
        : getValue && value[0] !== getValue(value[0])
          ? getValue(value[0])
          : calcOptions.find(o => o.value === value[0])?.title || `${value}`,
    [value, conditionValue, filterObject, operator, getValue]
  );
  const triggerProps = {
    ref: buttonRef,
    ...(isSelectTrigger
      ? {
          hasValidValue: Boolean(value?.length || filterObject[valueOperator]?.length > 0 || filterObject?.value !== undefined),
          processedValue: value,
          displayValue,
          tooltipValue,
          chipCount: isDateField
            ? undefined
            : filterObject
              ? conditionValue[operator].length - 1
              : value.length
                ? value.length - 1
                : undefined,
        }
      : {}),
    open,
    label: label as React.ReactNode,
    size: size as 'small' | 'xSmall' | 'medium',
    disabled,
    selectIcon,
    onClick: () => {
      setOpen(true);
      onOpen();
      if (!supportedType) {
        enqueueSnackbar('Unsupported Filter Type', { variant: 'error' });
      }
    },
  } as const;

  const isTextField = !isNumberField && !isDateField;
  const innerContent = isDateField ? (
    <AvDateRangePicker
      value={filterObject.value}
      activePresetValue={filterObject.preset}
      onChange={value => {
        if (value.value === undefined) {
          onChange(field, []);
          return;
        }
        if (isDateRangeEmpty(value.value) || value.value === null) {
          onChange(field, [encodeURIComponent(JSON.stringify({ value: 'empty' }))]);
          return;
        }
        if (isDateRangeNotEmpty(value.value)) {
          onChange(field, [encodeURIComponent(JSON.stringify({ value: 'notEmpty' }))]);
          return;
        }
        onChange(field, [encodeURIComponent(JSON.stringify(value))]);
      }}
      onClose={handleClose}
      showAsCalendar
      isRange
      showRelativeOptions
      isFilterExpression={isFilterExpression}
      showMinutesAndHours={shouldShowMinutesAndHours}
    />
  ) : (
    <>
      {initialFilter && !isNumberField && (
        <Box sx={{ py: 1, px: 2 }}>
          <RadioButton options={viewOptions} value={mode} onChange={setMode} />
        </Box>
      )}
      <Divider />
      {(isTextField || isBooleanField) && (
        <Box sx={{ px: 1, mt: 1, pb: 1, display: mode === 'list' ? 'block' : 'none' }}>
          <Select
            showOnlyAutoComplete
            size="small"
            acceptNullValue
            label={label as React.ReactNode}
            variant="filter"
            loading={isLoadingOptions}
            {...(isSeverity && { renderOption: ({ value }: any) => <SeverityItem value={value} /> })}
            isMultiple
            showInput={isTextField}
            options={calcOptions}
            value={filterObject ? [] : isBooleanField ? value?.map(JSON.parse) : value}
            onChange={val => {
              onChange([field], val);
              if (type === 'string') {
                onFilterChange?.({
                  expression: { fieldName: field, stringCondition: val.length > 0 ? { in: { values: val } } : { contains: '' } },
                });
              }
            }}
            totalOptionsCount={totalRowCount}
            renderOption={
              field.endsWith('.source_names')
                ? ({ value }: any) => <ItemWithLogo variant={IconVariant.sourcesMapByName} type={value} textStyle={{ overflow: 'auto' }} />
                : undefined
            }
            filterFunc={
              isSchemaField
                ? (options, { inputValue }) => {
                    setSearch(inputValue);
                    return options;
                  }
                : undefined
            }
          />
        </Box>
      )}
      <Box sx={{ gap: 1, p: 3, display: mode === 'condition' || !isTextField ? 'block' : 'none', maxHeight: 380, overflowY: 'auto' }}>
        {initialFilter &&
          conditionValue[operator].map((operand, index, operands) => (
            /* eslint-disable-next-line react/no-array-index-key */
            <Box key={index}>
              <Box
                sx={{
                  ...flex.itemsCenter,
                  p: '12px',
                  border: `1px solid ${palette.colors.neutrals[300]}`,
                  gap: 1,
                  borderRadius: '6px',
                  // @ts-ignore
                  '.MuiPaper-root': theme => ({ ...theme.components.MuiAutocomplete.styleOverrides.paper }),
                }}>
                <Select
                  size="small"
                  style={{ width: 170, input: { fontWeight: 600 } }}
                  isRequired
                  muiProps={{ disablePortal: disablePortalInOperatorSelect }}
                  placeholder="Select Operator"
                  value={getOperator(operand.operator)}
                  onChange={value => handleSelectConditionChange(value, index)}
                  options={getFilterOptionsWithLegacy(filterOperatorOptions[type], operand.operator)}
                />
                <FilterInput
                  value={operand.value}
                  operator={operand.operator}
                  numberField={isNumberField}
                  inputWidth={210}
                  onChange={value => {
                    setErrors([]);
                    setConditionValue(prev => setNestedValue(`${operator}.${index}.value`, prev, value));
                  }}
                  options={filterOperatorOptions[type]}
                  error={errors[index]?.value}
                />
                <IconButton
                  disabled={operands.length === 1 && conditionValue[operator][index].value === ''}
                  onClick={() => {
                    if (operands.length === 1) {
                      setConditionValue(prev => setNestedValue(`${operator}.${index}.value`, prev, ''));
                      onChange(field, []);
                      onFilterChange?.({
                        expression: {
                          fieldName: field,
                          [ConditionType[type]]: { [defaultOperator[ConditionType[type]]]: defaultValue[ConditionType[type]]() },
                        },
                      });
                    } else {
                      setConditionValue(prev => ({ [operator]: prev[operator].filter((o, i) => i !== index) }));
                    }
                    setErrors([]);
                  }}>
                  <Delete />
                </IconButton>
              </Box>
              <Box sx={{ ...flex.center, py: 1 }}>
                {operands.length > 1 && index !== operands.length - 1 && (
                  <ToggleButtonGroup
                    color="white"
                    value={operator}
                    onChange={() => setConditionValue(prev => renameProp(operator, operator === 'and' ? 'or' : 'and', prev))}
                    size="xSmall"
                    exclusive>
                    <ToggleButton value="and">
                      <Box
                        sx={theme => ({
                          fontWeight: 600,
                          fontSize: 13,
                          color: operator === 'and' ? getOperatorColor(theme, 'and') : 'inherit',
                        })}>
                        AND
                      </Box>
                    </ToggleButton>
                    <ToggleButton value="or">
                      <Box
                        sx={theme => ({
                          fontWeight: 600,
                          fontSize: 13,
                          color: operator === 'or' ? getOperatorColor(theme, 'or') : 'inherit',
                        })}>
                        OR
                      </Box>
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Box>
            </Box>
          ))}
        {isMultipleCondition && (
          <Box sx={flex.center}>
            <AddOperandButton
              operator={operator}
              isAdd
              onClick={() => setConditionValue(prev => ({ [operator]: [...prev[operator], initialFilter] }))}
            />
          </Box>
        )}
      </Box>
      <Divider orientation="horizontal" />
      {(mode === 'condition' || isNumberField) && (
        <Box sx={{ p: 2, ...flex.justifyBetweenCenter }}>
          <Button
            onClick={() => {
              setErrors([]);
              setConditionValue({ [operator]: [initialFilter] });
            }}
            size="small">
            Reset
          </Button>
          <div>
            <Button onClick={() => setOpen(false)} size="small">
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const validOperands = conditionValue[operator].filter(
                  ({ value, operator }) => typeof value === 'number' || isNullOperator(operator) || !!value
                );

                if (!validOperands.length) {
                  setConditionValue({ [operator]: [initialFilter] });
                  onChange(field, []);
                  onFilterChange?.(rawExpressionToFilter(field, conditionValue));
                  setOpen(false);
                } else {
                  const errorsExp = validOperands.map(({ value, operator }) => ({ operator, value: getErrors(value, operator) }));
                  if (errorsExp.some(({ value }) => value)) {
                    setErrors(errorsExp);
                  } else {
                    const nextConditionValue = { [operator]: validOperands };
                    setConditionValue(nextConditionValue);
                    onChange(field, [encodeURIComponent(JSON.stringify(nextConditionValue))]);
                    onFilterChange?.(rawExpressionToFilter(field, nextConditionValue));
                    setOpen(false);
                  }
                }
              }}>
              Apply
            </Button>
          </div>
        </Box>
      )}
    </>
  );

  return (
    <span key={field} ref={setRef}>
      {trigger ? React.cloneElement(trigger, triggerProps) : <SelectTrigger {...triggerProps} />}
      <FilterStyledPopper
        open={open}
        anchorEl={buttonRef.current}
        placement="bottom-start"
        popperOptions={{ strategy: 'fixed' }}
        sx={{ minWidth: '400px', width: 'fit-content', padding: 0 }}>
        <Box style={{ display: open ? 'block' : 'none' }} ref={popperInnerRef}>
          {innerContent}
        </Box>
      </FilterStyledPopper>
    </span>
  );
};

export const conditionTooltip = (operand, index, arr, operator) => (
  <span key={index}>
    <span style={{ fontWeight: 600 }}>{operatorLabels[operand.operator]} </span>
    <span>{getDisplayValue(operand.value)}</span>{' '}
    {index === arr.length - 1 ? (
      ''
    ) : (
      <Box
        component="span"
        sx={{
          fontWeight: 600,
          color: ({ palette }) => (operator === 'and' ? palette.colors.primary[400] : palette.colors.complementary[600]),
        }}>
        {operator.toUpperCase()}{' '}
      </Box>
    )}
  </span>
);

const defaultDateFormat = 'MMM dd, yyyy';

const shiftDate = (date: Date, unit: string, amount: number, op: 'add' | 'sub') => {
  if (unit === 'WEEKS') {
    return op === 'sub' ? subWeeks(date, amount) : addWeeks(date, amount);
  }
  if (unit === 'MONTHS') {
    return op === 'sub' ? subMonths(date, amount) : addMonths(date, amount);
  }
  return op === 'sub' ? subDays(date, amount) : addDays(date, amount);
};

export const formatDateValue = ({ value, dateFormat = defaultDateFormat }) => {
  if (value === undefined) {
    return '';
  }
  if (isDateRangeNotEmpty(value)) {
    return 'Is Not Empty';
  }

  if (value === null || isDateRangeEmpty(value)) {
    return 'Is Empty';
  }

  const relative = (() => {
    if (value?.relative && (value.relative.unit || value.relative.periodBehaviour)) {
      return value.relative;
    }
    if (value?.unit) {
      return value;
    }
    return null;
  })();

  if (relative) {
    const now = new Date();

    if (relative?.periodBehaviour) {
      const isRange = [PeriodBehavior.last, PeriodBehavior.previous].includes(relative.periodBehaviour);
      const start = isRange ? subDays(now, relative.value) : now;
      const end = isRange ? now : addDays(now, relative.value);
      return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
    }

    const { unit = 'DAYS', value: amount, direction } = relative;
    const isRange = direction === 'last' || direction === 'previous';
    const start = isRange ? shiftDate(now, unit, amount, 'sub') : now;
    const end = isRange ? now : shiftDate(now, unit, amount, 'add');
    return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
  }

  return isDateRangeValue(value)
    ? isEqual(new Date(value.from), new Date(epochDateString)) && value.to
      ? `Before ${format(add(new Date(value.to), { days: 1 }), dateFormat)}`
      : value.to
        ? `${format(new Date(value.from), dateFormat)} - ${format(new Date(value.to), dateFormat)}`
        : ''
    : '';
};

const defaultToEmpty = getValue => {
  try {
    return getValue() || '';
  } catch {
    return '';
  }
};
export const FilterInput = ({
  numberField,
  onChange,
  operator,
  value,
  options,
  size = 'small',
  placeholder = 'Type Value',
  width,
  inputWidth,
  disabled = isNullOperator(operator),
  error,
  optionsUseQueryProps,
  skeletonLoading = false,
  endAdornment,
  readOnly,
}: {
  numberField: boolean;
  operator: string;
  value: any;
  onChange: (value) => void;
  size?: 'small' | 'medium' | 'xSmall';
  placeholder?: string;
  width?: number;
  inputWidth?: number;
  options: FilterOption[];
  disabled?: boolean;
  error?: any;
  optionsUseQueryProps?: { options: any[]; isLoading: boolean };
  skeletonLoading?: boolean;
  endAdornment?: React.ReactNode;
  readOnly?: boolean;
}) => {
  const inputOptions = operator && options.find(({ value }) => value === getOperator(operator))?.inputOptions;
  return inputOptions ? (
    <Box sx={{ ...flex.itemsCenter, gap: 1, width }}>
      <TextInput
        sx={{ width: inputWidth, ...inputOptions.sx1 }}
        size={size}
        placeholder={inputOptions.placeholder1 || placeholder}
        value={defaultToEmpty(() => inputOptions.getValue1(value))}
        disableClearButton
        type={numberField ? 'number' : 'text'}
        onChange={newVal => onChange(inputOptions.onChange1(newVal, value))}
        error={error && inputOptions.getValue1(error)}
        inputProps={{ endAdornment, readOnly }}
      />
      {inputOptions.separator}
      <TextInput
        sx={{ width: inputWidth, ...inputOptions.sx2 }}
        size={size}
        placeholder={inputOptions.placeholder2 || placeholder}
        value={defaultToEmpty(() => inputOptions.getValue2(value))}
        disableClearButton
        onChange={newVal => onChange(inputOptions.onChange2(newVal, value))}
        error={error && inputOptions.getValue2(error)}
        inputProps={{ endAdornment, readOnly }}
        type={numberField ? 'number' : 'text'}
      />
    </Box>
  ) : optionsUseQueryProps && [StringConditionType.EQUALS, StringConditionType.NOT_EQUAL].includes(operator as StringConditionType) ? (
    <OpenSelect
      style={{ width: inputWidth }}
      size={size}
      options={optionsUseQueryProps.options}
      loading={optionsUseQueryProps.isLoading}
      skeletonLoading={skeletonLoading}
      value={value || null}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      disableClearButton
      isRequired
      optionsUseQueryProps={optionsUseQueryProps}
      readOnly={readOnly}
    />
  ) : (
    <TextInput
      style={{ width: inputWidth }}
      size={size}
      placeholder={placeholder}
      value={isJSON(value) ? '' : value}
      type={numberField ? 'number' : 'text'}
      disableClearButton={numberField}
      disabled={disabled}
      onChange={onChange}
      error={error}
      inputProps={{ endAdornment, readOnly }}
    />
  );
};

const emptyOperand = ['string', 'number', 'IP', 'FIX'].reduce(
  (obj, key) => ({
    ...obj,
    [key]: { operator: defaultOperator[ConditionType[key]], value: '' },
    [`${key}[]`]: { operator: `${defaultOperator[ConditionType[key]]}|ANY`, value: '' },
  }),
  {}
);

const findingStateOptions = [
  { title: 'Undetected', value: 'INACTIVE' },
  { title: 'Active', value: 'ACTIVE' },
];
const viewOptions = [
  { title: 'List', value: 'list' },
  { title: 'Condition', value: 'condition' },
];

const fieldsToIncludeMinutesAndHours = new Set(['alert.create_time', 'alert.first_seen']);
